import { useState, useCallback, useMemo } from 'react';

export default function useColumnReorder({ visibleColumns, onColumnOrderChange, controlledOrder } = {}) {
    const [internalOrder, setInternalOrder] = useState(null);
    const columnOrder = controlledOrder ?? internalOrder;

    const [dragState, setDragState] = useState({
        dragging: null,
        dropTarget: null,
        dropSide: null,
    });

    const reorderedColumns = useMemo(() => {
        if (!columnOrder || !columnOrder.length) return visibleColumns;
        const colMap = new Map(visibleColumns.map((c) => [c.key, c]));
        const ordered = [];

        // Sticky columns always come first in their original order
        for (const col of visibleColumns) {
            if (col.sticky) ordered.push(col);
        }

        // Non-sticky columns in specified order
        for (const key of columnOrder) {
            const col = colMap.get(key);
            if (col && !col.sticky) ordered.push(col);
        }

        // Any remaining columns not in the order array (newly added)
        for (const col of visibleColumns) {
            if (!ordered.includes(col)) ordered.push(col);
        }

        return ordered;
    }, [visibleColumns, columnOrder]);

    const updateOrder = useCallback(
        (newKeys) => {
            if (controlledOrder && onColumnOrderChange) {
                onColumnOrderChange(newKeys);
            } else {
                setInternalOrder(newKeys);
                if (onColumnOrderChange) onColumnOrderChange(newKeys);
            }
        },
        [controlledOrder, onColumnOrderChange]
    );

    const getDragHandleProps = useCallback(
        (colKey, isSticky) => {
            if (isSticky) return { draggable: false };

            return {
                draggable: true,
                onDragStart: (e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', colKey);
                    requestAnimationFrame(() => {
                        setDragState((prev) => ({ ...prev, dragging: colKey }));
                    });
                },
                onDragOver: (e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    const rect = e.currentTarget.getBoundingClientRect();
                    const midpoint = rect.left + rect.width / 2;
                    const side = e.clientX < midpoint ? 'left' : 'right';
                    setDragState((prev) => ({
                        ...prev,
                        dropTarget: colKey,
                        dropSide: side,
                    }));
                },
                onDragLeave: (e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                        setDragState((prev) => {
                            if (prev.dropTarget === colKey) {
                                return { ...prev, dropTarget: null, dropSide: null };
                            }
                            return prev;
                        });
                    }
                },
                onDrop: (e) => {
                    e.preventDefault();
                    const draggedKey = e.dataTransfer.getData('text/plain');
                    if (draggedKey === colKey) {
                        setDragState({ dragging: null, dropTarget: null, dropSide: null });
                        return;
                    }

                    const nonStickyKeys = reorderedColumns
                        .filter((c) => !c.sticky)
                        .map((c) => c.key);
                    const fromIndex = nonStickyKeys.indexOf(draggedKey);
                    if (fromIndex === -1) return;

                    const newKeys = nonStickyKeys.filter((k) => k !== draggedKey);
                    let targetIndex = newKeys.indexOf(colKey);
                    if (targetIndex === -1) {
                        setDragState({ dragging: null, dropTarget: null, dropSide: null });
                        return;
                    }

                    const rect = e.currentTarget.getBoundingClientRect();
                    const midpoint = rect.left + rect.width / 2;
                    if (e.clientX > midpoint) targetIndex += 1;

                    newKeys.splice(targetIndex, 0, draggedKey);
                    updateOrder(newKeys);
                    setDragState({ dragging: null, dropTarget: null, dropSide: null });
                },
                onDragEnd: () => {
                    setDragState({ dragging: null, dropTarget: null, dropSide: null });
                },
            };
        },
        [reorderedColumns, updateOrder]
    );

    const resetColumnOrder = useCallback(() => {
        updateOrder(null);
    }, [updateOrder]);

    return { columnOrder, reorderedColumns, dragState, getDragHandleProps, resetColumnOrder };
}
